import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { CalendarClock, CalendarRange, Users, Send, Mail, Loader2, FileText, Plus, X, Download, ListTodo, Eye } from "lucide-react";
import { ReportPreview } from "./ReportPreview";
import { V } from "@/lib/vocab";

const FREQS = [
  ["off", "Off"], ["daily", "Daily"], ["weekly", "Weekly"],
  ["biweekly", "Biweekly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"],
];
// iter295 — the DATA WINDOW the email covers, chosen independently of the send cadence.
const WINDOWS = [
  ["auto", "Match the schedule"],
  ["1d", "Last 24 hours"],
  ["7d", "Last 7 days"],
  ["14d", "Last 14 days"],
  ["30d", "Last 30 days"],
  ["90d", "Last 90 days"],
  ["mtd", "Month to date"],
  ["prev_month", "Previous month"],
  ["prev_quarter", "Previous quarter"],
];
const AUTO_LABEL = { daily: "yesterday", weekly: "last 7 days", biweekly: "last 14 days", monthly: "previous month", quarterly: "previous quarter" };
const DOW = [["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"], ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"]];
const hourLabel = (h) => { const ap = h < 12 ? "AM" : "PM"; const hr = h % 12 === 0 ? 12 : h % 12; return `${hr}:00 ${ap}`; };
const FREQ_LABEL = Object.fromEntries(FREQS);
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const scheduleSentence = (sch) => {
  const f = sch.frequency || "off";
  if (f === "off") return null;
  const time = hourLabel(sch.hour ?? 8);
  const dow = DOW.find(([v]) => v === String(sch.day_of_week ?? 0))?.[1] || "Monday";
  const dom = ordinal(sch.day_of_month ?? 1);
  switch (f) {
    case "daily": return `every day at ${time}`;
    case "weekly": return `every ${dow} at ${time}`;
    case "biweekly": return `every other ${dow} at ${time}`;
    case "monthly": return `on the ${dom} of each month at ${time}`;
    case "quarterly": return `on the ${dom} of each quarter at ${time}`;
    default: return null;
  }
};
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ROLE_LABEL = { hoa_manager: "Manager", board_member: "Board", company_admin: "Administrator", regional_manager: "Regional", super_admin: "Super admin", platform_admin: "Platform", operator: "Operator" };

export function ReportSchedule({ tenantId, readOnly }) {
  const [sch, setSch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [togglingId, setTogglingId] = useState("");
  const [preview, setPreview] = useState(null); // {frequency, window, include_pending} | {start, end}

  useEffect(() => {
    if (!tenantId) return;
    setSch(null);
    api.get(`/tenants/${tenantId}/report/schedule`).then((r) => setSch(r.data)).catch(() => setSch(null));
  }, [tenantId]);

  const set = (patch) => setSch((s) => ({ ...s, ...patch }));

  // Every save sends the FULL state — the backend PUT resets `frequency` to "off" when it's
  // omitted, so partial saves (e.g. just recipients) would silently turn scheduling off.
  const putSchedule = async (patch, successMsg) => {
    setSaving(true);
    try {
      const body = {
        frequency: sch.frequency,
        day_of_week: sch.day_of_week ?? 0,
        day_of_month: sch.day_of_month ?? 1,
        hour: sch.hour ?? 8,
        attach_pdf: sch.attach_pdf !== false,
        include_pending: sch.include_pending !== false,
        window: sch.window || "auto",
        extra_recipients: (sch.extra_recipients || []).map((e) => ({ name: e.name || "", email: e.email })),
        ...patch,
      };
      const r = await api.put(`/tenants/${tenantId}/report/schedule`, body);
      setSch(r.data);
      if (successMsg) toast.success(successMsg);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the schedule");
    } finally {
      setSaving(false);
    }
  };

  const save = () => putSchedule({}, sch.frequency === "off" ? "Automatic emails turned off" : "Report schedule saved");

  const addExternal = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address"); return; }
    if ((sch.extra_recipients || []).some((e) => e.email === email)) { toast.error("That email is already on the list"); return; }
    const next = [...(sch.extra_recipients || []), { name: newName.trim(), email }];
    const r = await putSchedule({ extra_recipients: next.map((e) => ({ name: e.name || "", email: e.email })) }, "Recipient added");
    if (r) { setNewName(""); setNewEmail(""); }
  };

  const removeExternal = async (email) => {
    const next = (sch.extra_recipients || []).filter((e) => e.email !== email);
    await putSchedule({ extra_recipients: next.map((e) => ({ name: e.name || "", email: e.email })) }, "Recipient removed");
  };

  // Super Admins / Managers directly add, remove, or reset a TEAM MEMBER on the shared report.
  // The override wins over the person's own notification setting (state "auto" drops it).
  const setRecipient = async (userId, state) => {
    setTogglingId(userId);
    try {
      const r = await api.put(`/tenants/${tenantId}/report/recipients`, { user_id: userId, state });
      setSch(r.data);
      toast.success(state === "auto" ? "Reset to their own choice" : state === "on" ? "Added to the report" : "Removed from the report");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update recipients"); }
    finally { setTogglingId(""); }
  };

  const send = async (test, extra = {}, key = null) => {
    setSending(key || (test ? "test" : "all"));
    try {
      const r = await api.post(`/tenants/${tenantId}/report/send-now`, { test, ...extra });
      const per = r.data.period ? ` (${r.data.period})` : "";
      if (test) {
        if (r.data.sent) toast.success(`Report sent to ${r.data.to}${r.data.pdf ? " with PDF" : ""}${per}`);
        else toast.warning(r.data.note || "Couldn't send — check email is configured");
      }
      else if (r.data.async) toast.success(`Report${per} is going out to ${r.data.recipients} recipient(s) — the final count lands in your activity log`);
      else toast.success(`Report${per} sent to ${r.data.sent} of ${r.data.recipients} recipient(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the report"); }
    finally { setSending(""); }
  };

  const downloadRange = async () => {
    if (!(rangeStart && rangeEnd && rangeStart <= rangeEnd)) return;
    setDownloading(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/report/download`, { start: rangeStart, end: rangeEnd }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${V.lc("org")}-report-${rangeStart}-to-${rangeEnd}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Couldn't build the PDF — please try again");
    } finally {
      setDownloading(false);
    }
  };

  if (!sch) return <div className="flex items-center gap-2 text-ink-500 py-24 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading schedule…</div>;

  const freq = sch.frequency || "off";
  const disabled = readOnly;
  const externals = sch.extra_recipients || [];
  const team = sch.team || [];
  const included = team.filter((m) => m.included);
  const todayStr = new Date().toISOString().slice(0, 10);
  const rangeValid = rangeStart && rangeEnd && rangeStart <= rangeEnd;

  return (
    <div className="max-w-2xl space-y-5" data-testid="report-schedule">
      <div className="rounded-2xl border border-sand-200 bg-white p-5 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><CalendarClock className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
          <div>
            <h3 className="font-heading font-bold text-ink">Emailed automatically</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Send this report to your managers and {V.grp()} on a schedule — so they stay informed without logging in.{freq === "off" ? " Off by default." : ""}</p>
          </div>
        </div>

        {/* iter294 — spell out that this is the SHARED report, not the reader's personal alerts,
            and hand them a direct link to their own Notifications so they stop looking here. */}
        <div data-testid="report-vs-personal-note" className="rounded-xl border border-sky-100 bg-sky-50/70 px-4 py-3 flex items-start gap-2.5">
          <Users className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-xs text-sky-900 leading-snug">
            <span className="font-semibold">This is a shared, {V.lc("org")}-wide report</span> — it goes to your whole management team and {V.grp()} on this schedule.
            To turn <span className="font-semibold">your own</span> alerts (new requests, escalations, field services) on or off, go to{" "}
            <Link to="/dashboard/account#notifications" data-testid="report-to-notifications" className="font-semibold underline underline-offset-2 hover:text-sky-700">your Notifications settings</Link>.
          </div>
        </div>

        {/* Frequency */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-2">How often</div>
          <div className="flex flex-wrap gap-2">
            {FREQS.map(([v, l]) => (
              <button key={v} type="button" data-testid={`report-freq-${v}`} disabled={disabled}
                onClick={() => set({ frequency: v })}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors disabled:opacity-60 ${
                  freq === v ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-sand-200 hover:border-ink/40"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* When */}
        {freq !== "off" && (
          <div className="flex flex-wrap items-end gap-4" data-testid="report-when-row">
            {(freq === "weekly" || freq === "biweekly") && (
              <label className="text-sm">
                <span className="block text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-1">Day of week</span>
                <select data-testid="report-dow" disabled={disabled} value={String(sch.day_of_week ?? 0)} onChange={(e) => set({ day_of_week: Number(e.target.value) })}
                  className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                  {DOW.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            )}
            {(freq === "monthly" || freq === "quarterly") && (
              <label className="text-sm">
                <span className="block text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-1">Day of month</span>
                <select data-testid="report-dom" disabled={disabled} value={String(sch.day_of_month ?? 1)} onChange={(e) => set({ day_of_month: Number(e.target.value) })}
                  className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
            )}
            <label className="text-sm">
              <span className="block text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-1">Time</span>
              <select data-testid="report-hour" disabled={disabled} value={String(sch.hour ?? 8)} onChange={(e) => set({ hour: Number(e.target.value) })}
                className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                {Array.from({ length: 24 }, (_, h) => h).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </label>
            <span className="text-xs text-muted-foreground pb-2">{V.one("org")} time zone</span>
          </div>
        )}

        {/* What each email covers — the DATA WINDOW, independent of send cadence (iter295) */}
        {freq !== "off" && (
          <div data-testid="report-window-row">
            <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-2">What each email covers</div>
            <select data-testid="report-window" disabled={disabled} value={sch.window || "auto"}
              onChange={(e) => set({ window: e.target.value })}
              className="rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
              {WINDOWS.map(([v, l]) => <option key={v} value={v}>{l}{v === "auto" ? ` (${AUTO_LABEL[freq] || "matches cadence"})` : ""}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1.5">
              The time span the report summarizes — separate from how often it&rsquo;s sent. A <b>daily</b> email set to <b>Last 7 days</b> gives a fresh rolling week every morning, instead of a thin single day.
            </p>
          </div>
        )}

        {/* What's waiting on you — the old separate "daily digest" email, folded into this report */}
        <div className="flex items-center justify-between gap-3 rounded-xl border border-sand-200 bg-sand-50/60 px-4 py-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <ListTodo className="h-4 w-4 text-redline-500 mt-0.5 shrink-0" strokeWidth={1.75} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">Include “Waiting on you right now”</div>
              <div className="text-xs text-muted-foreground">Adds the live to-do counts — unanswered conversations, urgent requests, approvals, signatures, overdue renewals — the same numbers as the sidebar. Set the cadence to <span className="font-semibold">daily</span> and this is your morning digest; there is no separate digest email.</div>
            </div>
          </div>
          <Switch data-testid="report-include-pending" checked={sch.include_pending !== false} disabled={disabled}
            onCheckedChange={(v) => set({ include_pending: v })} />
        </div>

        {/* PDF attachment */}
        <div className="flex items-center justify-between rounded-xl border border-sand-200 bg-sand-50/60 px-4 py-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <FileText className="h-4 w-4 text-redline-500 mt-0.5 shrink-0" strokeWidth={1.75} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">Attach a PDF copy</div>
              <div className="text-xs text-muted-foreground">Include a printable PDF of the report with every email — handy for {V.grp()} packets. On for everyone.</div>
            </div>
          </div>
          <Switch data-testid="report-attach-pdf" checked={sch.attach_pdf !== false} disabled={disabled}
            onCheckedChange={(v) => set({ attach_pdf: v })} />
        </div>

        {!disabled && (
          <button data-testid="report-schedule-save" onClick={save} disabled={saving}
            className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 text-sm hover:bg-redline-600 transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save schedule"}
          </button>
        )}
      </div>

      {/* Recipients */}
      <div className="rounded-2xl border border-sand-200 bg-white p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1"><Users className="h-4 w-4 text-redline-500" strokeWidth={1.75} /><h3 className="font-heading font-bold text-ink">Who gets it</h3></div>
        {/* When — the community's report policy, spelled out next to WHO gets it */}
        <div data-testid="report-when-summary"
          className={`mt-1 mb-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${
            freq === "off" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
          <CalendarClock className="h-4 w-4 shrink-0" strokeWidth={1.75} />
          {freq === "off"
            ? <span>Not emailed automatically — pick a frequency above to schedule it.</span>
            : <span>{FREQ_LABEL[freq]} — {scheduleSentence(sch)} <span className="font-normal opacity-70">({V.lc("org")} time zone)</span></span>}
        </div>
        <p className="text-sm text-muted-foreground">
          {freq === "off"
            ? <>These <b>{included.length}</b> team member{included.length === 1 ? "" : "s"} will get it once you turn on a schedule above.</>
            : <>These <b>{included.length}</b> team member{included.length === 1 ? "" : "s"} get it {scheduleSentence(sch)}.</>}
          {" "}{disabled
            ? <>Only a manager can change who&rsquo;s on the list.</>
            : <>Turn any teammate on or off below — your choice here overrides their personal setting.</>}
        </p>
        {team.length > 0 ? (
          <div className="mt-3 space-y-1.5" data-testid="report-team-list">
            {team.map((m) => (
              <div key={m.user_id} data-testid={`report-team-row-${m.user_id}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${m.no_email ? "border-amber-200 bg-amber-50/60" : "border-sand-200 bg-white"}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-ink">{m.name}</span>
                    <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink/55">{ROLE_LABEL[m.role] || m.role}</span>
                    {m.no_email && (
                      <span data-testid={`report-team-noemail-${m.user_id}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">No email</span>
                    )}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {m.no_email
                      ? <><span className="text-amber-700">No email on file — can&rsquo;t receive reports.</span>{!disabled && <> <Link data-testid={`report-team-addemail-${m.user_id}`} to="/dashboard/team" className="font-semibold text-redline-600 underline underline-offset-2 hover:text-ink">Add an email</Link></>}</>
                      : <>{m.email}
                        {m.overridden
                          ? <> · <span className="font-semibold text-redline-600">Set by manager</span>{!disabled && <> · <button data-testid={`report-team-reset-${m.user_id}`} onClick={() => setRecipient(m.user_id, "auto")} className="underline underline-offset-2 hover:text-ink">reset to their choice</button></>}</>
                          : <> · follows their own setting</>}</>}
                  </div>
                </div>
                {!m.no_email && (
                  <Switch data-testid={`report-team-toggle-${m.user_id}`} checked={m.included}
                    disabled={disabled || togglingId === m.user_id}
                    onCheckedChange={(v) => setRecipient(m.user_id, v ? "on" : "off")} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 text-xs text-muted-foreground" data-testid="report-team-empty">No team members on this {V.lc("org")} yet.</div>
        )}

        {/* External recipients */}
        <div className="mt-5 pt-5 border-t border-sand-100">
          <div className="text-sm font-semibold text-ink flex items-center gap-1.5"><Mail className="h-4 w-4 text-ink/40" /> External recipients</div>
          <p className="text-xs text-muted-foreground mt-0.5">Add people who don't log in — like a treasurer or accountant. They follow the {V.lc("org")} schedule above.</p>
          {externals.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="report-external-list">
              {externals.map((e) => (
                <div key={e.email} className="flex items-center gap-2 rounded-xl border border-sand-200 bg-white px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">{e.name || e.email}</div>
                    {e.name && <div className="text-xs text-muted-foreground truncate">{e.email}</div>}
                  </div>
                  {!disabled && (
                    <button data-testid={`report-external-remove-${e.email}`} onClick={() => removeExternal(e.email)} disabled={saving}
                      className="text-ink/40 hover:text-danger-600 shrink-0 disabled:opacity-50" title="Remove">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {!disabled && (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input data-testid="report-external-name" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Name (optional)" className="rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 sm:w-40" />
              <input data-testid="report-external-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addExternal()}
                placeholder="email@example.com" type="email"
                className="min-w-0 flex-1 rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button data-testid="report-external-add" onClick={addExternal} disabled={saving || !newEmail.trim()}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-ink text-white px-4 py-2 text-sm font-semibold hover:bg-ink-700 transition-colors disabled:opacity-50 shrink-0">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Send now */}
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="report-preview-btn"
          onClick={() => setPreview({ frequency: sch.frequency || "off", window: sch.window || "auto", include_pending: sch.include_pending !== false, attach_pdf: sch.attach_pdf !== false })}
          className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white px-5 py-2.5 text-sm font-semibold hover:bg-redline-600 transition-colors">
          <Eye className="h-4 w-4" /> Preview the email
        </button>
        <button data-testid="report-send-test" onClick={() => send(true)} disabled={!!sending}
          className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink transition-colors disabled:opacity-60">
          {sending === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send me a test
        </button>
        {!disabled && (
          <button data-testid="report-send-now" onClick={() => send(false)} disabled={!!sending}
            className="inline-flex items-center gap-2 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold hover:bg-ink-700 transition-colors disabled:opacity-60">
            {sending === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to everyone now
          </button>
        )}
        <p className="text-xs text-muted-foreground w-full flex items-center gap-1.5" data-testid="report-test-pdf-note">
          <FileText className="h-3.5 w-3.5 text-ink/40" />
          {sch.attach_pdf !== false
            ? "Preview shows tomorrow's email built from live data with the settings above (saved or not). Your test includes the PDF copy attached — exactly what recipients receive."
            : "Preview shows tomorrow's email built from live data with the settings above (saved or not). Turn on “Attach a PDF copy” above to include the printable PDF in your test."}
        </p>
      </div>
      <ReportPreview tenantId={tenantId} open={!!preview} onClose={() => setPreview(null)} params={preview || {}} />

      {/* On-demand custom-range report */}
      <div className="rounded-2xl border border-sand-200 bg-white p-5 sm:p-6" data-testid="report-custom-range">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><CalendarRange className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-ink">Build a report for specific dates</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Pick any date range and generate this same analytics report for that window — download it as a PDF or email it to yourself or the whole list. Great for {V.aGrp()} packet or a one-off review.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-1">From</span>
            <input type="date" data-testid="report-range-start" max={todayStr} value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
              className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </label>
          <label className="text-sm">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-1">To</span>
            <input type="date" data-testid="report-range-end" max={todayStr} value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
              className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </label>
          <button data-testid="report-range-preview" onClick={() => setPreview({ start: rangeStart, end: rangeEnd })}
            disabled={!rangeValid || downloading}
            className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink transition-colors disabled:opacity-50">
            <Eye className="h-4 w-4" /> Preview
          </button>
          <button data-testid="report-range-download" onClick={downloadRange}
            disabled={!rangeValid || downloading || !!sending}
            className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white px-5 py-2.5 text-sm font-semibold hover:bg-redline-600 transition-colors disabled:opacity-50">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </button>
          <button data-testid="report-range-send-me" onClick={() => send(true, { start: rangeStart, end: rangeEnd }, "range-test")}
            disabled={!rangeValid || !!sending || downloading}
            className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink transition-colors disabled:opacity-50">
            {sending === "range-test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email me this range
          </button>
          {!disabled && (
            <button data-testid="report-range-send-all" onClick={() => send(false, { start: rangeStart, end: rangeEnd }, "range-all")}
              disabled={!rangeValid || !!sending || downloading}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold hover:bg-ink-700 transition-colors disabled:opacity-50">
              {sending === "range-all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to everyone
            </button>
          )}
        </div>
        {rangeStart && rangeEnd && rangeStart > rangeEnd && (
          <p className="text-xs text-redline-600 mt-2">The start date must be on or before the end date.</p>
        )}
      </div>
    </div>
  );
}
